import { useMsal } from '@azure/msal-react';
import { AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest } from '../auth/authConfig';

export interface UseAuthResult {
  account: AccountInfo | null;
  isAuthenticated: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  acquireToken: (scopes?: string[]) => Promise<string>;
}

/**
 * Custom hook for authentication.
 * Provides login, logout, and token acquisition for API calls.
 */
export function useAuth(): UseAuthResult {
  const { instance, accounts } = useMsal();
  const account = accounts[0] || null;

  const login = async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await instance.logoutRedirect({ account });
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const acquireToken = async (scopes?: string[]): Promise<string> => {
    if (!account) {
      throw new Error('No account available for token acquisition');
    }

    const request = {
      scopes: scopes || loginRequest.scopes,
      account,
    };

    try {
      const response = await instance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        const response = await instance.acquireTokenPopup(request);
        return response.accessToken;
      }
      throw error;
    }
  };

  return {
    account,
    isAuthenticated: !!account,
    login,
    logout,
    acquireToken,
  };
}
